import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { LineItemsLoading } from '@components/bid_request/details/LineItemsLoading';
import { LotPageError } from '@features/buyer-line-items-pagination/components/lot-page-error';
import { LotPagePaginator } from '@features/buyer-line-items-pagination/components/lot-page-paginator';
import { DEFAULT_LOT_PAGINATION } from '@features/buyer-line-items-pagination/constants';
import { useLotPage } from '@features/buyer-line-items-pagination/hooks/use-lot-page';
import type { LotPageLoad } from '@features/buyer-line-items-pagination/types/lot-page';
import type { LotPageOptions } from '@features/buyer-line-items-pagination/types/lot-page-options';
import type { ID, LineItem, LotPagination } from 'types';
import styles from '@components/bid_request/details/LineItemsTable/LineItemsTable.module.scss';

interface PagedLotTableProps {
  deletedRowCount: number;
  eventId: ID;
  isPagingLocked: boolean;
  loadedRows?: LineItem[];
  lotId: ID;
  lotPagination?: LotPagination;
  lotRowCount?: number;
  onPageChange: (page: number, pageSize: number) => void;
  onPageLoaded: (load: LotPageLoad) => void;
  onPageReadFailed: () => void;
  onPageValidated: () => void;
  renderTable: (pageOptions: LotPageOptions) => ReactNode;
  shellRefreshCount: number;
}

/**
 * The flag-on lot table: owns the lot's page reads, and holds the grid back until the lot's first page has
 * landed. Once a page has landed the grid stays mounted, whatever rows a later render holds. A page past the end comes
 * back empty from the server (rows deleted elsewhere), and the lot steps back to the page before it; a cached page
 * shown before its check does not move the lot.
 */
export const PagedLotTable = ({
  deletedRowCount,
  eventId,
  isPagingLocked,
  loadedRows,
  lotId,
  lotPagination = DEFAULT_LOT_PAGINATION,
  lotRowCount,
  onPageChange,
  onPageLoaded,
  onPageReadFailed,
  onPageValidated,
  renderTable,
  shellRefreshCount,
}: PagedLotTableProps) => {
  const { currentPage, loadedPage, loadedPageSize, loads, pageIds, pageSize, total, validateAtLoads } = lotPagination;

  const { hasLoadedPage, isFetchingPage, isPageError, retry } = useLotPage({
    eventId,
    loads,
    lotId,
    onPageLoaded,
    page: currentPage,
    pageSize,
    shellRefreshCount,
  });
  const onRetry = useCallback(() => { retry(); }, [retry]);

  useEffect(() => {
    if (isPageError) onPageReadFailed();
  }, [isPageError, onPageReadFailed]);

  const isLoadedPagePastEnd = loads > 0 && loadedPage > 1 && pageIds.length === 0;
  const shouldStepBack = isLoadedPagePastEnd && !isPagingLocked && !isFetchingPage;
  useEffect(() => {
    if (shouldStepBack) onPageChange(loadedPage - 1, loadedPageSize);
  }, [loadedPage, loadedPageSize, onPageChange, shouldStepBack]);

  const pageOptions: LotPageOptions = useMemo(() => ({
    currentPage,
    isPageError,
    isPageLoading: loads > 0 && (currentPage !== loadedPage || pageSize !== loadedPageSize),
    isPagingLocked,
    isRetrying: isPageError && isFetchingPage,
    loadedPage,
    loadedPageSize,
    loadedRows,
    loads,
    onPageChange,
    onPageValidated,
    onRetry,
    pageSize,
    shouldValidatePage: validateAtLoads !== undefined && loads >= validateAtLoads,
    total: Math.max(0, (loads > 0 ? total : lotRowCount ?? 0) - deletedRowCount),
  }), [
    currentPage,
    deletedRowCount,
    isFetchingPage,
    isPageError,
    isPagingLocked,
    loadedPage,
    loadedPageSize,
    loadedRows,
    loads,
    lotRowCount,
    onPageChange,
    onPageValidated,
    onRetry,
    pageSize,
    total,
    validateAtLoads,
  ]);

  const showsTable = loads > 0 || (hasLoadedPage && !isPageError);

  if (showsTable) return <>{renderTable(pageOptions)}</>;
  if (isPageError) {
    return (
      <>
        <LotPageError isRetrying={isFetchingPage} lotId={lotId} onRetry={onRetry} />
        {(loads > 0 || lotRowCount !== undefined) && <LotPagePaginator lotId={lotId} pageOptions={pageOptions} />}
      </>
    );
  }

  return (
    <div className={styles.loadingContainer} data-testid={`${lotId}-line-items-table-loading`}>
      <LineItemsLoading />
    </div>
  );
};
